import { FC, useMemo, useState } from 'react';

import { useRecoilValue, useSetRecoilState } from 'recoil';

import { updateShopOrder } from '@shared/api/shopOrder/shopOrder.controller';
import { shopOrderListForSellerUrl } from '@shared/api/shopOrder/shopOrder.hook';
import {
  ShopOrderForSeller,
  ShopOrderSellerRequestStatus,
} from '@shared/api/shopOrder/shopOrder.interface';
import {
  isDepositLikePayment,
  isTossVbankPayment,
  sellerRequestStatusLabel,
} from '@shared/api/shopOrder/shopOrder.utils';
import { Button } from '@shared/components/Button';
import ConfirmModal from '@shared/components/ConfirmModal';
import {
  notificateError,
  notificateSuccess,
} from '@shared/plugIn/ant-notification/ant-notifiaction';
import { simpleOpenAtomFamily } from '@shared/state/atom';
import { notificationInstanceAtom } from '@shared/state/atom/notification.atom';
import { useAuth } from '@shared/state/hooks/useAuth';
import { DeliveryState, PaymentState } from '@shared/types';
import { formatDate } from '@shared/utils/formatDate';
import { deliveryStateToString } from '@shared/utils/stateUtils';
import { useQueryClient } from 'react-query';

import { DeliveredModal } from './DeliveredModal';
import { OrderManualRefundRequestModal } from './OrderManualRefundRequestModal';
import { OrderTaxInvoiceRequestModal } from './OrderTaxInvoiceRequestModal';
import { OrderVbankCancelModal } from './OrderVbankCancelModal';
import { OrderCanceldModal } from './ShopOrderCancelModal';

//* 면세계산서/수기환불 요청 상태 칩 색 — 라벨은 짧게 유지하고 색으로만 상태 구분
const requestChipClass = (status?: ShopOrderSellerRequestStatus) => {
  switch (status) {
    case 'Requested':
      return 'border-amber-500 bg-amber-50 text-amber-700';
    case 'Done':
      return 'border-green-600 bg-green-50 text-green-700';
    case 'Rejected':
      return 'border-red-500 bg-red-50 text-red-600';
    default:
      return 'border-gray-300 bg-white text-gray-700';
  }
};

interface Props {
  shopOrder: ShopOrderForSeller;
}

export const DeliveryStatusManageButton: FC<Props> = function DeliveryStatusManageButton({
  shopOrder,
}: Props) {
  const confirmOpenId = `deliveryStatusManageButton-${shopOrder._id}`;
  const deliveredOpenId = `deliveryStatusManageButton-delivered-${shopOrder._id}`;
  const cancelOpenId = `deliveryStatusManageButton-cancel-${shopOrder._id}`;
  const setOpen = useSetRecoilState(simpleOpenAtomFamily(confirmOpenId));
  const setDeliveredOpen = useSetRecoilState(simpleOpenAtomFamily(deliveredOpenId));
  const setCancelOpen = useSetRecoilState(simpleOpenAtomFamily(cancelOpenId));

  //* 가상계좌·무통장(계좌 입금) 결제 — PG 자동취소가 안 되므로 취소 버튼이
  //* 일반 취소 모달 대신 수기환불(계좌이체) 요청으로 진입한다.
  const depositLike = isDepositLikePayment(shopOrder.paymentDetail);
  //* 단, 토스 가상계좌만은 환불계좌를 함께 보내면 PG 자동취소가 된다 —
  //* 결제완료 건에 한해 환불계좌 입력 모달로 보낸다 (수기환불은 그 옆 칩으로 남겨둔다).
  const tossVbank = isTossVbankPayment(shopOrder.paymentDetail);
  const tossVbankCancelable =
    tossVbank && shopOrder.paymentDetail.status === PaymentState.PAID;
  const vbankCancelOpenId = `deliveryStatusManageButton-vbank-cancel-${shopOrder._id}`;
  const setVbankCancelOpen = useSetRecoilState(simpleOpenAtomFamily(vbankCancelOpenId));
  const taxInvoiceOpenId = `deliveryStatusManageButton-tax-invoice-${shopOrder._id}`;
  const setTaxInvoiceOpen = useSetRecoilState(simpleOpenAtomFamily(taxInvoiceOpenId));
  const manualRefundOpenId = `deliveryStatusManageButton-manual-refund-${shopOrder._id}`;
  const setManualRefundOpen = useSetRecoilState(simpleOpenAtomFamily(manualRefundOpenId));
  const manualRefundStatus = shopOrder.manualRefund?.status;
  //* 계좌환불 요청이 살아 있으면 서버가 자동환불을 400으로 막는다(이중 환불 방지).
  //* 버튼은 남기되(눌러야 사유를 읽을 수 있다) 주 동작이 아님을 색으로 알린다.
  const autoCancelBlocked =
    manualRefundStatus === 'Requested' || manualRefundStatus === 'Done';
  const taxInvoiceStatus = shopOrder.taxInvoiceRequest?.status;
  const [updateStatus, setUpdateState] = useState(DeliveryState.BEFORE_PREPARING);
  const notifiacationInstance = useRecoilValue(notificationInstanceAtom);

  const { sellerRole } = useAuth();
  const queryClient = useQueryClient();

  const onStatusUpdateClick = async (status: DeliveryState) => {
    const updateShopOrder_ = {
      _id: shopOrder._id,
      'deliveryDetail.status': status,
    };
    try {
      await updateShopOrder(updateShopOrder_, sellerRole());
      await queryClient.invalidateQueries(shopOrderListForSellerUrl(sellerRole()));
      notificateSuccess(notifiacationInstance, '상태변경에 성공하였습니다.');
    } catch (err: any) {
      notificateError(notifiacationInstance, '실패');
    }
  };

  const buttonContent = useMemo(() => {
    if (shopOrder.deliveryDetail.status === DeliveryState.BEFORE_PREPARING) {
      return (
        <Button
          className="button-sm border-none bg-cyan-600 px-2 text-white hover:bg-cyan-700"
          onClick={() => {
            setUpdateState(DeliveryState.PREPARING);
            setOpen(true);
          }}
        >
          상품 준비 시작
        </Button>
      );
    } else if (shopOrder.deliveryDetail.status === DeliveryState.PREPARING) {
      return (
        <Button
          className="button-sm border-none bg-cyan-600 px-2 text-white hover:bg-cyan-700"
          onClick={async () => {
            setUpdateState(DeliveryState.DELIVERYING);
            setOpen(true);
          }}
        >
          배송 시작
        </Button>
      );
    } else if (shopOrder.deliveryDetail.status === DeliveryState.DELIVERYING) {
      return (
        <Button
          className="button-sm border-none bg-green-500 px-2 text-white hover:bg-green-600"
          onClick={async () => {
            setDeliveredOpen(true);
          }}
        >
          배송 완료
        </Button>
      );
    } else if (shopOrder.deliveryDetail.status === DeliveryState.DELIVERED) {
      return (
        <button
          type="button"
          className="button-sm center-box filled-gray-50 border border-gray-300 px-2 text-gray-700 hover:bg-gray-100"
          onClick={() => setDeliveredOpen(true)}
        >
          <div className="text-center">
            <p>
              {formatDate(shopOrder.deliveryDetail.deliveredAt, {
                dateSeparater: '.',
                contains: {
                  year: false,
                  day: true,
                  hours: true,
                  minutes: true,
                },
              })}
            </p>
            <p>배송완료 됨 (시각수정)</p>
          </div>
        </button>
      );
    } else return null;
  }, [
    setDeliveredOpen,
    setOpen,
    shopOrder.deliveryDetail.deliveredAt,
    shopOrder.deliveryDetail.status,
  ]);

  const cancelButton = useMemo(() => {
    if (shopOrder.paymentDetail.status === PaymentState.CONFIRMED) {
      return (
        <div className="button-sm center-box filled-gray-50 border border-gray-300 px-2 text-gray-700">
          구매확정 완료됨
        </div>
      );
    } else if (
      [PaymentState.CANCELED, PaymentState.CANCEL_REQUESTED].includes(
        shopOrder.paymentDetail.status,
      )
    ) {
      //* 토스 자동환불로 취소된 건(cancelAccount 기록 있음)은 이체할 게 남아있지 않다 —
      //* 수기환불 진입을 막고 환불 행선지만 보여준다 (수기요청 이력이 있으면 그쪽 칩이 우선).
      const cancelAccount = shopOrder.paymentDetail.cancelAccount;
      if (cancelAccount && !manualRefundStatus) {
        return (
          <div
            className="button-sm center-box filled-gray-50 border border-gray-300 px-2 text-gray-700"
            title={`환불계좌 ${cancelAccount.bank} ${cancelAccount.accountNumber} ${cancelAccount.holderName}`}
          >
            {shopOrder.paymentDetail.status === PaymentState.CANCELED
              ? '취소됨(자동환불)'
              : '취소 요청 중'}
          </div>
        );
      }
      //* 취소돼도 이체는 수동이므로 입금결제 건은 칩에서 수기환불 요청으로 진입 가능
      if (depositLike) {
        return (
          <button
            type="button"
            className={`button-sm center-box border px-2 ${requestChipClass(
              manualRefundStatus,
            )}`}
            onClick={() => setManualRefundOpen(true)}
          >
            {manualRefundStatus
              ? `환불 ${sellerRequestStatusLabel[manualRefundStatus]}`
              : shopOrder.paymentDetail.status === PaymentState.CANCELED
              ? '취소됨'
              : '취소 요청 중'}
          </button>
        );
      }
      return (
        <div className="button-sm center-box filled-gray-50 border border-gray-300 px-2 text-gray-700">
          {shopOrder.paymentDetail.status === PaymentState.CANCELED
            ? '취소됨'
            : '취소 요청 중'}
        </div>
      );
    } else if (tossVbankCancelable) {
      //* 토스 가상계좌 결제완료 건 — 환불계좌를 받아 PG 자동환불로 취소
      return (
        <Button
          className={
            autoCancelBlocked
              ? 'button-sm border border-gray-300 bg-white px-2 text-gray-500'
              : 'button-sm border-none bg-warn px-2 text-white hover:bg-warn'
          }
          title={
            autoCancelBlocked
              ? '계좌환불 요청이 있어 자동환불이 막혀 있습니다 — 요청을 반려한 뒤 가능'
              : undefined
          }
          onClick={async () => {
            setVbankCancelOpen(true);
          }}
        >
          취소(자동환불)
        </Button>
      );
    } else if (depositLike) {
      //* 입금결제 건은 일반 취소(PG 자동환불)가 동작하지 않음 — 계좌환불신청으로 진입
      return (
        <Button
          className={
            manualRefundStatus
              ? `button-sm border px-2 ${requestChipClass(manualRefundStatus)}`
              : 'button-sm border-none bg-warn px-2 text-white hover:bg-warn'
          }
          onClick={async () => {
            setManualRefundOpen(true);
          }}
        >
          {manualRefundStatus
            ? `환불 ${sellerRequestStatusLabel[manualRefundStatus]}`
            : '취소(계좌)'}
        </Button>
      );
    } else {
      return (
        <Button
          className="button-sm border-none bg-warn px-6 text-white hover:bg-warn"
          onClick={async () => {
            setCancelOpen(true);
          }}
        >
          취소
        </Button>
      );
    }
  }, [
    depositLike,
    tossVbankCancelable,
    autoCancelBlocked,
    manualRefundStatus,
    setCancelOpen,
    setManualRefundOpen,
    setVbankCancelOpen,
    shopOrder.paymentDetail.status,
    shopOrder.paymentDetail.cancelAccount,
  ]);

  //* 토스 가상계좌 자동환불 옆에 남겨두는 수기환불 진입 — 자동환불이 거절되거나(은행 점검 등)
  //* 이미 수기이체로 처리 중인 건을 위해. 상태가 있으면 상태 칩으로 보인다.
  const vbankManualRefundButton = useMemo(() => {
    if (!tossVbankCancelable) return null;
    return (
      <button
        type="button"
        className={`button-sm center-box border px-2 ${requestChipClass(
          manualRefundStatus,
        )}`}
        title="자동환불이 안 될 때 — 운영사가 직접 계좌이체하는 수기환불 요청"
        onClick={() => setManualRefundOpen(true)}
      >
        {manualRefundStatus
          ? `환불 ${sellerRequestStatusLabel[manualRefundStatus]}`
          : '수기환불'}
      </button>
    );
  }, [tossVbankCancelable, manualRefundStatus, setManualRefundOpen]);

  //* 계산서요청 버튼 — 주문 반려(취소) 우측, 입금결제 + 결제완료(또는 기존 요청 존재) 시
  const taxInvoiceButton = useMemo(() => {
    if (!depositLike) return null;
    const eligible =
      [PaymentState.PAID, PaymentState.CONFIRMED].includes(
        shopOrder.paymentDetail.status,
      ) || !!taxInvoiceStatus;
    if (!eligible) return null;
    return (
      <button
        type="button"
        className={`button-sm center-box border px-2 ${requestChipClass(
          taxInvoiceStatus,
        )}`}
        onClick={() => setTaxInvoiceOpen(true)}
      >
        계산서요청
      </button>
    );
  }, [depositLike, taxInvoiceStatus, setTaxInvoiceOpen, shopOrder.paymentDetail.status]);

  return (
    <>
      <div className="flex w-full justify-between gap-1.5">
        <div className="flex flex-wrap gap-1.5">
          {cancelButton}
          {vbankManualRefundButton}
          {taxInvoiceButton}
        </div>
        {buttonContent}
      </div>
      <ConfirmModal
        openId={confirmOpenId}
        title={deliveryStateToString(updateStatus) + '로 변경'}
        buttonTitle={'상태 업데이트'}
        onClick={async () => {
          await onStatusUpdateClick(updateStatus);
        }}
      />
      <DeliveredModal openId={deliveredOpenId} shopOrder={shopOrder}></DeliveredModal>
      <OrderCanceldModal openId={cancelOpenId} shopOrder={shopOrder}></OrderCanceldModal>
      {tossVbank && (
        <OrderVbankCancelModal openId={vbankCancelOpenId} order={shopOrder} />
      )}
      {depositLike && (
        <>
          <OrderTaxInvoiceRequestModal openId={taxInvoiceOpenId} order={shopOrder} />
          <OrderManualRefundRequestModal openId={manualRefundOpenId} order={shopOrder} />
        </>
      )}
    </>
  );
};
